import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { BREAKPOINTS } from 'Style/breakpoints';
import { SPACING } from 'Style/spacing';
import { CONTENT_STORY_HEADING_TYPES } from 'Style/typography';
import { TEXT_COLORS, DefaultTextColorStyle } from 'Style/colors';

// Utils
import StoryboardRenderingUtil from 'Utils/content/storyboard-rendering-util';

// HOCs
import withShare from '../../hocs/withShare';
import withFlip from '../../hocs/withFlip';
import withFollowAuthor from '../../hocs/withFollowAuthor';
import withT from 'ComponentLibrary/hocs/withT';

import connector from 'Utils/connector';
import connectModal from 'Webapp/shared/app/connectors/connectModal';
import connectAmp from 'Webapp/shared/app/connectors/connectAmp';
import connectResponsive from 'Webapp/shared/app/connectors/connectResponsive';

// Components
import ItemSocialActions from '../../components/item/item-social-actions';
import Badge from '../../components/badge';
import StoryboardAuthorAttribution from './author-attribution';
import { StyledItemMetric } from 'Webapp/shared/app/components/item-actions/item-metric';
import DefaultItemActionButton from 'Webapp/shared/app/components/item-actions/default-item-action-button';
import { Meatball } from 'ComponentLibrary/icons/meatball';

const StoryboardFooterWrapper = styled.div({
  backgroundColor:
    'var(--color--surface-new-storyboard-default-cover) //Overridden in code by cover item',
  color: TEXT_COLORS.overlay,

  '.button--base': {
    color: TEXT_COLORS.overlay,
  },
});

const StoryboardFooterContentWrapper = styled.div(
  {
    margin: `0 ${SPACING.LARGE}`,
    padding: `${SPACING.BASE4X} 0`,
    display: 'grid',
    gridTemplateColumns: 'auto 0.2fr',
    [StyledItemMetric]: DefaultTextColorStyle(true),

    '@media (min-width: 992px)': {
      margin: 'auto',
      maxWidth: '960px',
    },
    '.item-social-actions': {
      alignItems: 'start',
      marginTop: '-8px',
    },
  },

  BREAKPOINTS.tabletPortraitUp({
    padding: `${SPACING.BASE6X} 0`,
  }),
);
const StyledItemSocialActions = styled(ItemSocialActions)({
  [DefaultItemActionButton]: DefaultTextColorStyle(true, false),
  [Meatball]: {
    backgroundColor: TEXT_COLORS.overlay,
  },
  '.social-actions': {
    backgroundColor: 'inherit',
  },
  '.social-actions__content': {
    backgroundColor: 'inherit',
  },
});

const FooterTitle = styled.h2({
  ...CONTENT_STORY_HEADING_TYPES.LARGE,
  gridColumn: '1 / 2',
  gridRow: '2 / 3',
  color: TEXT_COLORS.overlay,
});

const FooterActions = styled.div({
  gridColumn: '2 / 3',
  gridRow: '2 / 3',
});

/**
 * NOTE: StoryboardAuthorAttribution itself is a styled component
 * and we are overriding it here StoryboardAuthorAttribution must proxy a
 * className prop to the relevant div.
 */
const StoryboardAttribution = styled(StoryboardAuthorAttribution)({
  gridColumn: '1 / 2',
  gridRow: '3/ 4',
  marginTop: SPACING.BASE4X,
  marginBottom: '0px',
  'a, a:hover': {
    color: TEXT_COLORS.overlay,
  },
});

const StoryboardBadge = styled(Badge)(
  DefaultTextColorStyle(true),
  {
    gridColumn: '1 / 2',
    gridRow: '1 / 2',
  },
  BREAKPOINTS.phone({ paddingBotton: SPACING.BASE }),
  BREAKPOINTS.tabletPortraitUp({ paddingBottom: SPACING.LARGE }),
);
function StoryboardFooter({
  section,
  coverItem,
  authorSection,
  isAmp,
  isPhone,
  t,
}) {
  const coverImage = StoryboardRenderingUtil.getCoverImage(coverItem, section);

  const backgroundStyle = StoryboardRenderingUtil.getBackgroundColorStyle(
    coverImage,
    0,
  );

  return (
    <StoryboardFooterWrapper
      className="storyboard-footer"
      style={backgroundStyle}
    >
      <StoryboardFooterContentWrapper className="storyboard-footer__content-wrapper">
        <StoryboardBadge isOverlay>{t('storyboard')}</StoryboardBadge>
        <FooterTitle>{section.title}</FooterTitle>

        <StoryboardAttribution
          section={section}
          coverItem={coverItem}
          authorSection={authorSection}
        />
        {!isAmp && coverItem && !isPhone && (
          <FooterActions className="storyboard-footer__actions">
            <StyledItemSocialActions
              item={coverItem}
              section={section}
              articleRemoteId={coverItem.articleRemoteId}
              hideCounts
            />
          </FooterActions>
        )}
      </StoryboardFooterContentWrapper>
    </StoryboardFooterWrapper>
  );
}

StoryboardFooter.propTypes = {
  section: PropTypes.object.isRequired,
  coverItem: PropTypes.object,
  authorSection: PropTypes.object,
  showModal: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
  onFlip: PropTypes.func.isRequired,
  isAmp: PropTypes.bool,
  t: PropTypes.func.isRequired,
  isPhone: PropTypes.bool.isRequired,
};

StoryboardFooter.defaultProps = {
  coverItem: undefined,
  authorSection: null,
  isAmp: false,
};

export default connector(
  connectResponsive,
  connectAmp,
  connectModal,
)(withT(withFollowAuthor(withShare(withFlip(StoryboardFooter)))));
